import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useToast } from "../context/ToastContext";
import type { FolderDTO } from "@shared/types";

export interface FolderTreeNode extends FolderDTO {
  children: FolderTreeNode[];
}

function buildTree(folders: FolderDTO[]): FolderTreeNode[] {
  const byId = new Map<string, FolderTreeNode>();
  folders.forEach((f) => byId.set(f.id, { ...f, children: [] }));
  const roots: FolderTreeNode[] = [];
  byId.forEach((node) => {
    if (node.parentId && byId.has(node.parentId)) byId.get(node.parentId)!.children.push(node);
    else roots.push(node);
  });
  const sortRec = (nodes: FolderTreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

export function useFolders() {
  const toast = useToast();
  const [folders, setFolders] = useState<FolderDTO[]>([]);
  const [tree, setTree] = useState<FolderTreeNode[]>([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const res = await api.listFolders();
      setFolders(res.folders);
      setTree(buildTree(res.folders));
      setUncategorizedCount(res.uncategorizedCount);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not load folders.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const createFolder = useCallback(
    async (name: string, parentId: string | null) => {
      await api.createFolder(name, parentId);
      toast.success("Folder created.");
      await reload();
    },
    [reload, toast],
  );

  const renameFolder = useCallback(
    async (id: string, name: string) => {
      await api.renameFolder(id, name);
      toast.success("Folder renamed. Image URLs inside it have changed.");
      await reload();
    },
    [reload, toast],
  );

  const deleteFolder = useCallback(
    async (id: string) => {
      await api.deleteFolder(id);
      toast.success("Folder deleted.");
      await reload();
    },
    [reload, toast],
  );

  return {
    folders,
    tree,
    uncategorizedCount,
    loading,
    reload,
    createFolder,
    renameFolder,
    deleteFolder,
  };
}
